import React, { useState, useEffect } from 'react';
import { cargosPermitidos, nomeAmigavel } from '../../../utils/cargos';

/**
 * Componente que agrupa todos os modais de usuários (criar, editar, deletar)
 * 
 * @param {Object} props
 * @param {Object} props.user - Usuário logado
 * @param {Array} props.condominios - Lista de condomínios disponíveis
 * @param {string} props.condominioNome - Nome do condomínio ativo
 * @param {boolean} props.showCreateModal - Controle de exibição do modal de criação
 * @param {boolean} props.showEditModal - Controle de exibição do modal de edição
 * @param {boolean} props.showDeleteConfirm - Controle de exibição do modal de exclusão
 * @param {Object|null} props.editingUser - Usuário sendo editado
 * @param {Object|null} props.userToDelete - Usuário a ser deletado
 * @param {Function} props.onCreateSubmit - Callback ao submeter criação
 * @param {Function} props.onEditSubmit - Callback ao submeter edição
 * @param {Function} props.onDeleteConfirm - Callback ao confirmar exclusão
 * @param {Function} props.onCloseCreate - Callback ao fechar modal de criação
 * @param {Function} props.onCloseEdit - Callback ao fechar modal de edição
 * @param {Function} props.onCloseDelete - Callback ao fechar modal de exclusão
 */
const UsuarioForms = ({
  user,
  condominios,
  condominioNome,
  showCreateModal,
  showEditModal,
  showDeleteConfirm,
  editingUser,
  userToDelete,
  onCreateSubmit,
  onEditSubmit,
  onDeleteConfirm,
  onCloseCreate,
  onCloseEdit,
  onCloseDelete
}) => {
  // Estados do formulário de criação
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [cargo, setCargo] = useState("");
  const [condominiosSelecionados, setCondominiosSelecionados] = useState([]);
  const [erroCondominios, setErroCondominios] = useState(null);
  
  const cargosDisponiveis = cargosPermitidos(user?.cargo);
  
  // Resetar erro de condominios quando cargo muda para admin
  useEffect(() => {
    if (cargo === 'admin') {
      setErroCondominios(null);
    }
  }, [cargo]);
  
  // Popular campos ao abrir modal de edição
  useEffect(() => {
    if (showEditModal && editingUser) {
      setUsername(editingUser.username);
      setCargo(editingUser.cargo);
      setPassword(""); // Não preencher senha na edição
    }
  }, [showEditModal, editingUser]);
  
  const resetForm = () => {
    setUsername("");
    setPassword("");
    setCargo("");
    setCondominiosSelecionados([]);
    setErroCondominios(null);
  };
  
  const handleCreateSubmit = (e) => {
    e.preventDefault();
    
    if (!username || !password || !cargo) {
      return;
    }
    
    // Validação: admin criando sindico precisa selecionar condomínios
    if (user?.cargo === 'admin' && cargo === 'sindico' && condominiosSelecionados.length === 0) {
      setErroCondominios("Síndico deve ter acesso a pelo menos um condomínio [erro-001]");
      return;
    }
    
    setErroCondominios(null);
    
    // Determinar condominios a vincular
    let condominiosParaVincular = [];
    if (user?.cargo === 'admin' && ['admin', 'sindico'].includes(cargo)) {
      condominiosParaVincular = condominiosSelecionados;
    }
    
    onCreateSubmit({ username, password, cargo, condominioIds: condominiosParaVincular });
    resetForm();
  };
  
  const handleEditSubmit = (e) => {
    e.preventDefault();
    
    if (!username || !cargo) {
      return;
    }
    
    const payload = { username, cargo };
    
    // Só incluir senha se foi fornecida
    if (password && password.trim().length > 0) {
      payload.password = password;
    }
    
    onEditSubmit(payload);
    resetForm();
  };
  
  const handleCloseCreate = () => {
    resetForm();
    onCloseCreate();
  };
  
  const handleCloseEdit = () => {
    resetForm();
    onCloseEdit();
  };
  
  return (
    <>
      {/* Modal de Criação */}
      {showCreateModal && (
        <div className="usuarios-modal-overlay">
          <div className="usuarios-modal">
            <form className="usuarios-form" onSubmit={handleCreateSubmit}>
              <div className="usuarios-form-header">
                <h2>Novo Usuário</h2>
                <button type="button" className="usuarios-modal-close" onClick={handleCloseCreate}>
                  ×
                </button>
              </div>
              
              <div className="usuarios-form-group">
                <label>
                  Nome de usuário <span className="usuarios-required">*</span>
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Digite o nome de usuário"
                  required
                />
              </div>
              
              <div className="usuarios-form-group">
                <label>
                  Senha <span className="usuarios-required">*</span>
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  minLength={6}
                  required
                />
              </div>
              
              <div className="usuarios-form-group">
                <label>
                  Cargo <span className="usuarios-required">*</span>
                </label>
                <select value={cargo} onChange={(e) => setCargo(e.target.value)} required>
                  <option value="">Selecione o cargo</option>
                  {cargosDisponiveis.map((cargoOpcao) => (
                    <option key={cargoOpcao} value={cargoOpcao}>
                      {nomeAmigavel(cargoOpcao)}
                    </option>
                  ))}
                </select>
                <p className="usuarios-help-text">
                  {user?.cargo === 'sindico' && 'Como síndico, você só pode criar usuários com cargo de Portaria'}
                </p>
              </div>
              
              {/* Mostrar checkboxes se admin está criando sindico/admin */}
              {cargo && user?.cargo === 'admin' && ['admin', 'sindico'].includes(cargo) ? (
                <div className="usuarios-condominio-info usuarios-condominio-multi">
                  <div className="usuarios-condominio-nome">
                    🏢 Seleção de Condominios {cargo === 'sindico' && <span style={{color: '#dc2626'}}>*</span>}
                  </div>
                  <div className="usuarios-condominio-hint">
                    Selecione os condominios que este {nomeAmigavel(cargo)} terá acesso{cargo === 'sindico' ? ' (obrigatório)' : ' (deixe vazio para todos)'}
                  </div>
                  <div className="usuarios-condominios-checkboxes">
                    {condominios.map(cond => (
                      <label key={cond.id} className="usuarios-checkbox-item">
                        <input
                          type="checkbox"
                          checked={condominiosSelecionados.includes(cond.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCondominiosSelecionados([...condominiosSelecionados, cond.id]);
                            } else {
                              setCondominiosSelecionados(condominiosSelecionados.filter(id => id !== cond.id));
                            }
                          }}
                        />
                        <span>{cond.nome}</span>
                      </label>
                    ))}
                  </div>
                  {condominiosSelecionados.length === 0 && cargo !== 'sindico' && (
                    <p className="usuarios-help-text usuarios-help-info">
                      ℹ️ Nenhum selecionado = acesso a todos os {condominios.length} condominios
                    </p>
                  )}
                  {erroCondominios && (
                    <p className="usuarios-help-text usuarios-help-error">
                      ❌ {erroCondominios}
                    </p>
                  )}
                </div>
              ) : (
                <div className="usuarios-condominio-info">
                  <div className="usuarios-condominio-nome">
                    📍 {condominioNome}
                  </div>
                  <div className="usuarios-condominio-hint">
                    O usuário será criado com acesso a este condomínio
                  </div>
                </div>
              )}
              
              <div className="usuarios-form-actions">
                <button type="button" className="usuarios-btn-cancel" onClick={handleCloseCreate}>
                  Cancelar
                </button>
                <button type="submit" className="usuarios-btn-submit">
                  Criar Usuário
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal de Edição */}
      {showEditModal && editingUser && (
        <div className="usuarios-modal-overlay">
          <div className="usuarios-modal">
            <form className="usuarios-form" onSubmit={handleEditSubmit}>
              <div className="usuarios-form-header">
                <h2>Editar Usuário</h2>
                <button type="button" className="usuarios-modal-close" onClick={handleCloseEdit}>
                  ×
                </button>
              </div>
              
              <div className="usuarios-form-group">
                <label>
                  Nome de usuário <span className="usuarios-required">*</span>
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Digite o nome de usuário"
                  required
                />
              </div>
              
              <div className="usuarios-form-group">
                <label>Senha (deixe em branco para manter a atual)</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Nova senha (opcional)"
                  minLength={6}
                />
              </div>
              
              <div className="usuarios-form-group">
                <label>
                  Cargo <span className="usuarios-required">*</span>
                </label>
                <select value={cargo} onChange={(e) => setCargo(e.target.value)} required>
                  <option value="">Selecione o cargo</option>
                  {cargosDisponiveis.map((cargoOpcao) => (
                    <option key={cargoOpcao} value={cargoOpcao}>
                      {nomeAmigavel(cargoOpcao)}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="usuarios-form-actions">
                <button type="button" className="usuarios-btn-cancel" onClick={handleCloseEdit}>
                  Cancelar
                </button>
                <button type="submit" className="usuarios-btn-submit">
                  Salvar Alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && userToDelete && (
        <div className="usuarios-modal-overlay">
          <div className="usuarios-modal usuarios-modal-small">
            <div className="usuarios-form">
              <div className="usuarios-form-header">
                <h2>Confirmar Exclusão</h2>
                <button 
                  type="button" 
                  className="usuarios-modal-close" 
                  onClick={onCloseDelete}
                >
                  ×
                </button>
              </div>
              
              <p className="usuarios-confirm-text">
                Tem certeza que deseja excluir o usuário <strong>{userToDelete.username}</strong>?
                Esta ação não pode ser desfeita.
              </p>
              
              <div className="usuarios-form-actions">
                <button 
                  type="button" 
                  className="usuarios-btn-cancel" 
                  onClick={onCloseDelete}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="usuarios-btn-delete-confirm" 
                  onClick={onDeleteConfirm}
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UsuarioForms;
